import {
  type AiSuggestionView,
  type ChangeRaidStatusInput,
  type CreateRaidInput,
  type EscalateRiskInput,
  type EscalateRiskResult,
  type LinkRaidTaskInput,
  type RaidItemView,
  type RaidLogView,
  type RaidType,
  type RiskSignalReport,
  type UpdateRaidInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const raidApi = {
  log: (projectId: string, type?: RaidType) =>
    apiFetch<RaidLogView>(`/projects/${projectId}/raid${type ? `?type=${type}` : ''}`),

  create: (projectId: string, input: CreateRaidInput) =>
    apiFetch<RaidItemView>(`/projects/${projectId}/raid`, { method: 'POST', body: input }),

  update: (raidId: string, input: UpdateRaidInput) =>
    apiFetch<RaidItemView>(`/raid/${raidId}`, { method: 'PATCH', body: input }),

  changeStatus: (raidId: string, input: ChangeRaidStatusInput) =>
    apiFetch<RaidItemView>(`/raid/${raidId}/status`, { method: 'PATCH', body: input }),

  escalate: (raidId: string, input: EscalateRiskInput = {}) =>
    apiFetch<EscalateRiskResult>(`/raid/${raidId}/escalate`, { method: 'POST', body: input }),

  remove: (raidId: string) => apiFetch<void>(`/raid/${raidId}`, { method: 'DELETE' }),

  // ── Liên kết với công việc (APM-RAID-e) ───────────────────────────────────

  link: (raidId: string, input: LinkRaidTaskInput) =>
    apiFetch<RaidItemView>(`/raid/${raidId}/links`, { method: 'POST', body: input }),

  unlink: (linkId: string) => apiFetch<void>(`/raid/links/${linkId}`, { method: 'DELETE' }),

  // ── APM-702 ───────────────────────────────────────────────────────────────

  signals: (projectId: string) => apiFetch<RiskSignalReport>(`/projects/${projectId}/risk-signals`),

  suggestions: (projectId: string) =>
    apiFetch<AiSuggestionView[]>(`/projects/${projectId}/risk-suggestions`),

  /**
   * Chạy skill qua cửa chung `/ai/skills/:name/run`, không qua endpoint riêng:
   * đó là chỗ duy nhất kiểm hạn mức và ghi `ai_usage_log` (ADR-006).
   */
  scan: (projectId: string, prompt: string, runId?: string) =>
    apiFetch<AiSuggestionView>('/ai/skills/risk_scan/run', {
      method: 'POST',
      body: { projectId, prompt, ...(runId ? { runId } : {}) },
    }),

  acceptSuggestion: (suggestionId: string, indexes: number[]) =>
    apiFetch<RaidItemView[]>(`/raid/suggestions/${suggestionId}/accept`, {
      method: 'POST',
      body: { indexes },
    }),

  dismissSuggestion: (suggestionId: string, reason: string) =>
    apiFetch<AiSuggestionView>(`/raid/suggestions/${suggestionId}/dismiss`, {
      method: 'POST',
      body: { reason },
    }),
};

/**
 * Tiền tố `raid` khớp phần đầu của tên thông báo (`raid.risk_high`):
 * `useNotificationStream` invalidate theo đúng tiền tố đó, nên cảnh báo rủi ro
 * cao tự làm mới bảng mà không cần xử lý riêng.
 */
export const raidKeys = {
  log: (projectId: string, type: RaidType | 'ALL') => ['raid', projectId, type] as const,
  signals: (projectId: string) => ['raid', projectId, 'signals'] as const,
  suggestions: (projectId: string) => ['raid', projectId, 'suggestions'] as const,
};
